// Period selector + custom date range strip. Sits in the sticky topbar
// next to the breadcrumb. Switching period invalidates every per-page
// aggregate and recomputes downstream charts.
//
// The trailing-window vocabulary is shared with the data-layer bucketing
// logic (data.js PERIOD_DAYS / bucketByLabel): Today = 1 day, 7d =
// day-of-week, 30d = 4 weeks, 90d / 6m / 12m = months.
//
// The two calendar-month options are not in that vocabulary. They resolve to
// explicit start/end dates in PERIOD_TO_RANGE and take the same path through
// the pages as a hand-picked custom range.
//
// Postgres mapping: period token is passed to analytics endpoints as a
// query param; the server resolves it to a date range.
// Integration hook: no API surface; controls upstream queries via
// the shell's `period` state.
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Box } from '@mui/material';

// Period pill. Active pill inverts to the foreground colour; the sliding
// highlight behind it is a shared layoutId, so only the border and text
// colours switch here.
const pillSx = (active) => ({
    position: 'relative',
    fontSize: 12,
    fontWeight: 600,
    px: 1.25,
    py: 0.5,
    borderRadius: '4px',
    border: '1px solid',
    transition: 'color 150ms, border-color 150ms',
    cursor: 'pointer',
    fontFamily: 'Inter, sans-serif',
    flexShrink: 0,
    ...(active
        ? { borderColor: 'var(--text)', color: 'var(--bg)' }
        : {
              borderColor: 'var(--border2)',
              backgroundColor: 'transparent',
              color: 'var(--muted)',
              '&:hover': { color: 'var(--text)', borderColor: 'var(--dim)' },
          }),
});

// Period tokens, ordered as displayed. Every token here must be a key of
// PERIOD_TO_RANGE; the trailing windows are additionally keys of
// PERIOD_DATA / PERIOD_DAYS in data.js.
const PERIODS = ['This month', 'Last month', 'Today', '7d', '30d', '90d', '6m', '12m'];

const dateInputStyle = {
    fontSize: 11,
    fontWeight: 500,
    lineHeight: '1.4',
    fontFamily: 'Inter, sans-serif',
    borderRadius: 4,
    border: '1px solid var(--border2)',
    background: 'transparent',
    color: 'var(--muted)',
    cursor: 'pointer',
    outline: 'none',
    minWidth: 0,
    padding: '3px 7px',
};

// BD-1196: a custom range is drafted locally and only loads on Apply, so each
// keystroke in a date input doesn't fire a fresh set of report queries.
const isRange = (start, end) => !!start && !!end && start <= end;

function CustomRange({ startDate, setStartDate, endDate, setEndDate, setPeriod, disabled, style }) {
    const [draftStart, setDraftStart] = useState(startDate);
    const [draftEnd, setDraftEnd] = useState(endDate);
    useEffect(() => { setDraftStart(startDate); setDraftEnd(endDate); }, [startDate, endDate]);
    const edited = draftStart !== startDate || draftEnd !== endDate;
    const apply = () => { setStartDate(draftStart); setEndDate(draftEnd); setPeriod(''); };

    return (
        <div style={style}>
            <input type="date" value={draftStart} disabled={disabled} onChange={(e) => setDraftStart(e.target.value)} style={dateInputStyle} />
            <span style={{ fontSize: 10, color: 'var(--dim)' }}>-</span>
            <input type="date" value={draftEnd} disabled={disabled} onChange={(e) => setDraftEnd(e.target.value)} style={dateInputStyle} />
            {edited && (
                <Box component="button" sx={pillSx(false)} disabled={disabled || !isRange(draftStart, draftEnd)} onClick={apply}>
                    Apply
                </Box>
            )}
        </div>
    );
}

export default function FilterBar({
    period, setPeriod, startDate, setStartDate, endDate, setEndDate,
    go: _go, noPadX = false, disabled = false,
}) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', columnGap: 12, rowGap: 8, flexWrap: 'wrap', padding: noPadX ? '8px 0' : '8px 24px' }}>
            {/* Period pills + date range, grouped together on the left */}
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                {PERIODS.map((p) => (
                    <Box
                        component="button"
                        key={p}
                        sx={pillSx(period === p)}
                        style={{ background: period === p ? 'var(--text)' : 'transparent', opacity: disabled ? 0.5 : 1 }}
                        disabled={disabled}
                        onClick={() => setPeriod(p)}
                    >
                        {period === p && (
                            <motion.span
                                layoutId="active-period-pill"
                                style={{ position: 'absolute', inset: 0, borderRadius: 4, zIndex: -1, background: 'var(--text)' }}
                                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                            />
                        )}
                        {p}
                    </Box>
                ))}
                <CustomRange
                    startDate={startDate} setStartDate={setStartDate}
                    endDate={endDate} setEndDate={setEndDate}
                    setPeriod={setPeriod} disabled={disabled}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: 4 }}
                />
            </div>

        </div>
    );
}
